import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { SharedModule } from "../../shared.module";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { takeUntil } from "rxjs";
import { FormInputComponent } from "../ui-components/forms/form-input/form-input.component";
import { FormContainerComponent } from "../ui-components/forms/form-container/form-container.component";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { PaymentDialogBase } from "../payment-dialog-base/payment-dialog-base";
import { PaymentDialogModel, OfflinePaymentDialogContext } from "src/app/models/payment-dialog.model";
import { IPaymentAttribute } from "src/app/services/model/pos.api.model";
import { IPayment } from "src/app/services/model/get-trip-data.model";

@Component({
  selector: "app-offline-card-payment-dialog",
  templateUrl: "./offline-card-payment-dialog.component.html",
  styleUrls: ["./offline-card-payment-dialog.component.scss"],
  imports: [
    TranslateModule,
    CommonModule,
    SharedModule,
    FormsModule,
    FormInputComponent,
    FormContainerComponent,
    ReactiveFormsModule,
  ],
  standalone: true,
})
export class OfflineCardPaymentDialogComponent extends PaymentDialogBase {
  private static readonly CARD_PREFIX_PATTERN = /^\d{6}$/;
  private static readonly CARD_SUFFIX_PATTERN = /^\d{4}$/;
  private static readonly AUTHORIZATION_CODE_PATTERN = /^[a-zA-Z0-9]{6}$/;

  public offlinePaymentDisplayName: string | null = null;

  get tenderType(): TenderType {
    return TenderType.Card;
  }

  get hasPresetAmount(): boolean {
    const amount = this.dialogData.initialAmountToPay;
    return amount !== null && amount !== undefined && Number(amount) > 0;
  }

  get presetAmountFormatted(): string | null {
    const amount = Number(this.dialogData.initialAmountToPay);
    return Number.isFinite(amount) ? this.currencyPipe.transform(amount, this.stateService.currencyCode()) || null : null;
  }

  private get dialogData(): PaymentDialogModel {
    return this.data as PaymentDialogModel;
  }

  private get offlineContext(): OfflinePaymentDialogContext | null {
    return this.dialogData.offlinePaymentContext ?? null;
  }

  protected getPaymentTranslationKey(): string {
    return "webPos.OfflineCardPayment";
  }

  override async ngOnInit() {
    this.initializeOfflineForm();
    this.offlinePaymentDisplayName = await this.resolveDisplayName();
  }

  protected override isFormValid(): boolean {
    const suffix = this.confirmForm.get("cardSuffix");
    const authCode = this.confirmForm.get("authorizationCode");
    const reason = this.confirmForm.get("overpaymentReason");

    if (!suffix?.valid || !authCode?.valid) {
      return false;
    }

    if (this.isOverpaymentAmount() && !this.isOverpaymentOverrideEnabled()) {
      return false;
    }

    if (this.isOverpaymentOverrideEnabled() && !reason?.valid) {
      return false;
    }

    return this.resolveAmount() > 0;
  }

  public override async onConfirm() {
    if (!this.isFormValid()) {
      this.confirmForm.markAllAsTouched();
      this.confirmForm.updateValueAndValidity();
      return;
    }

    const paymentTypes = await this.getAvailablePaymentTypes();
    const paymentType = paymentTypes.find(pt => pt.TenderType === this.tenderType);

    if (!paymentTypes.some(type => type.Name === (this.offlineContext?.paymentName || paymentType?.Name))) return;
    this.dialogResult.Amount = this.resolveAmount();
    this.dialogResult.PaymentType = this.offlineContext?.paymentType ?? this.tenderType;
    this.dialogResult.PaymentName = this.offlineContext?.paymentName || paymentType?.Name || "";
    this.dialogResult.Currency = this.offlineContext?.currency || this.loginService.user.CurrencyCode;

    this.buildPaymentAttributes();

    this.dialogRef.close(this.dialogResult as IPayment);
  }

  public override canUseOverpaymentOverride(): boolean {
    return !this.isReturn && this.isOverpaymentAmount();
  }

  private initializeOfflineForm(): void {
    this.confirmForm = new FormGroup({
      amountToPay: new FormControl<number | null>(null),
      selectAllCheckbox: new FormControl<boolean>(false),
      amountReceived: new FormControl<number | null>(null),
      allowOverpayment: new FormControl<boolean>(false),
      overpaymentReason: new FormControl<string | null>(null),
      cardSuffix: new FormControl<string>("", [
        Validators.required,
        Validators.pattern(OfflineCardPaymentDialogComponent.CARD_SUFFIX_PATTERN),
      ]),
      authorizationCode: new FormControl<string>("", [
        Validators.required,
        Validators.pattern(OfflineCardPaymentDialogComponent.AUTHORIZATION_CODE_PATTERN),
      ]),
    });

    this.updateOverpaymentReasonValidation();
    this.confirmForm.get("allowOverpayment")?.valueChanges
      .pipe(takeUntil(this.destroyObservable$))
      .subscribe(() => {
        if (!this.isOverpaymentOverrideEnabled()) {
          this.confirmForm.get("overpaymentReason")?.setValue(null, { emitEvent: false });
        }

        this.updateOverpaymentReasonValidation();
      });

    this.confirmForm.get("amountToPay")?.valueChanges
      .pipe(takeUntil(this.destroyObservable$))
      .subscribe(() => {
        if (!this.canUseOverpaymentOverride()) {
          this.resetOverpaymentOverride();
        }
      });

    if (this.hasPresetAmount) {
      this.confirmForm.get("amountToPay")?.disable({ emitEvent: false });
    }
  }

  private resolveAmount(): number {
    if (this.hasPresetAmount) {
      return Number(this.dialogData.initialAmountToPay);
    }
    return Number(this.confirmForm.get("amountToPay")?.value);
  }

  private isOverpaymentAmount(): boolean {
    const remainingToPay = this.dialogData.remainingToPay;
    if (remainingToPay === null || remainingToPay === undefined) {
      return false;
    }

    const amount = this.resolveAmount();
    const remaining = Number(remainingToPay);
    return Number.isFinite(amount)
      && Number.isFinite(remaining)
      && Math.round(amount * 100) > Math.round(remaining * 100);
  }

  private buildPaymentAttributes(): void {
    const cardSuffix = (this.confirmForm.get("cardSuffix")?.value || "").toString().trim();
    const authorizationCode = (this.confirmForm.get("authorizationCode")?.value || "").toString().trim();

    const attributes: IPaymentAttribute[] = [
      { Key: "Type", Value: "OFFLINE" },
      { Key: "CardSuffix", Value: cardSuffix },
      { Key: "AuthorizationCode", Value: authorizationCode },
    ];

    // Merge context attributes (TenderType, TenderSubType, etc.)
    const contextAttributes = this.offlineContext?.paymentAttributes || [];
    contextAttributes.forEach(ctxAttr => {
      if (!ctxAttr?.Key) return;
      const existing = attributes.find(a => a.Key === ctxAttr.Key);
      if (existing) {
        existing.Value = ctxAttr.Value;
      } else {
        attributes.push(ctxAttr);
      }
    });

    // Ensure TenderType is always present
    if (!attributes.some(a => a.Key === "TenderType") && this.offlineContext?.paymentType !== undefined) {
      attributes.push({ Key: "TenderType", Value: String(this.offlineContext.paymentType) });
    }

    this.dialogResult.PaymentAttributes = this.appendOverpaymentAttributes(attributes);
  }

  private async resolveDisplayName(): Promise<string | null> {
    const displayName = this.offlineContext?.paymentDisplayName?.trim();
    if (displayName) {
      return displayName;
    }

    const paymentName = this.offlineContext?.paymentName?.trim();
    if (!paymentName) {
      return null;
    }

    const paymentTypes = await this.getAvailablePaymentTypes();
    const matched = paymentTypes.find(type => type.Name.trim() === paymentName);
    return matched?.Description?.trim() || paymentName;
  }
}
